/** Kill switch contract: the switch store's keys and name, the stage tag on every Lambda and the migration runners left on. */

/** Keys the site's switch store holds. */
export const SWITCH_KEYS = {
   appOff: 'gap-app-off',
   holdingPage: 'gap-holding-page',
   bypass: 'gap-bypass',
} as const

/** Value a switch holds while it is on. */
export const SWITCH_ON = 'on'

/** Tag naming the stage on every Lambda function a stage deploys. */
export const STAGE_TAG = 'gap:stage'

/** Path the site answers with whether the holding page shows. */
export const STATUS_PATH = '/__status'

/** Query parameter carrying the holding page's bypass secret. */
export const BYPASS_PARAM = 'gap_bypass'

/** The SST version whose rendered viewer-request handler was measured, and that handler's size in bytes. */
export const MEASURED_SST = { version: '3.19.3', handlerBytes: 7392 } as const

const STORE_NAME_PREFIX = 'gap-'
const STORE_NAME_SUFFIX = '-switches'
const STORE_NAME_MAX_LENGTH = 64
const SHORTENED_STAGE_LENGTH = 42

const FNV_OFFSET_BASIS = 0x811c9dc5
const FNV_PRIME = 0x01000193

/**
 * Hashes text with 32-bit FNV-1a over its UTF-8 bytes.
 *
 * @param text The text to hash.
 * @returns The hash as 8 lowercase hex characters.
 */
function fnv1aHex(text: string): string {
   const hash = [...new TextEncoder().encode(text)].reduce((current, byte) => Math.imul(current ^ byte, FNV_PRIME) >>> 0, FNV_OFFSET_BASIS)
   return hash.toString(16).padStart(8, '0')
}

/**
 * Names a stage's switch store, shortening a long stage with its hash to stay within 64 characters.
 *
 * @param stage The stage, such as `prod-v3`.
 * @returns The store name, such as `gap-prod-v3-switches`.
 */
export function siteSwitchStoreName(stage: string): string {
   const fullName = `${STORE_NAME_PREFIX}${stage}${STORE_NAME_SUFFIX}`
   if (fullName.length <= STORE_NAME_MAX_LENGTH) return fullName
   return `${STORE_NAME_PREFIX}${stage.slice(0, SHORTENED_STAGE_LENGTH)}-${fnv1aHex(stage)}${STORE_NAME_SUFFIX}`
}

/**
 * Names the SSM parameters holding a stage's two migration runner function names.
 *
 * @param stage The stage, such as `prod-v3`.
 * @returns The Postgres migration runner's parameter, then the data migration runner's.
 */
export function migrationRunnerParameters(stage: string): [string, string] {
   return [`/sst-v3/${stage}/postgres/migration-function-name`, `/sst-v3/${stage}/jobs/migrations-function-name`]
}

/** A function's current tags and the stage it belongs to. */
export interface StageTagInputs {
   tags: Record<string, string> | undefined
   stage: string
}

/**
 * Adds the stage tag to a function's tags.
 *
 * @param inputs The function's current tags, if any, and the stage it belongs to.
 * @returns The tags with `STAGE_TAG` set to the stage.
 */
export function withStageTag({ tags, stage }: StageTagInputs): Record<string, string> {
   return { ...tags, [STAGE_TAG]: stage }
}
